import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { cn } from '@/utils/utils.ts';
import { useEffect, useRef } from 'react';
import {
  COMMON_DEVICE_FIELDS,
  DEVICE_TYPE_CODE_BY_NAME,
  PLANT_MEASUREMENT_CODE_BY_NAME,
} from '@/features/devices/schemas/devices.schemas.ts';
import {
  DEVICE_EVENTS_METADATA,
  PLANT_FIELDS_METADATA,
} from '@/features/devices/schemas/devices.metadata.ts';
import { Input } from '@/components/ui/input.tsx';
import { EventRuleFormData } from '@/features/event-rules/schemas/create-event-rules.schema.ts';
import {
  getAvailableParameters,
  getParameterLabel,
  OPERATORS,
} from '@/features/event-rules/schemas/event-rules.utils';

export function EditEventRuleConditionsForm() {
  const form = useFormContext<EventRuleFormData>();
  const watchRuleType = form.watch('ruleType');
  const watchDeviceType = form.watch('deviceType');

  const { fields } = useFieldArray({
    control: form.control,
    name: 'conditions',
  });

  return (
    <div className="@container flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Conditions</h2>
      </div>

      {fields.map((field, index) => (
        <div className="group flex flex-col" key={field.id}>
          <div
            className={cn(
              'grid grid-cols-2 flex-wrap items-start gap-2 gap-y-0',
              fields.length > 1
                ? '@min-[800px]:grid-cols-[repeat(3,minmax(170px,_1fr))_40px]'
                : '@min-[800px]:grid-cols-3'
            )}
          >
            <div className="flex-2">
              <FormField
                control={form.control}
                name={`conditions.${index}.fact`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="required">Parameter</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={true}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select parameter">
                            {getParameterLabel({
                              watchDeviceType,
                              watchRuleType,
                              fieldValue: field.value,
                            })}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAvailableParameters({
                          watchRuleType,
                          watchDeviceType,
                        }).map((param) => (
                          <SelectItem key={String(param.id)} value={param.id}>
                            {param.name} {param.unit && `(${param.unit})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex-2">
              <FormField
                control={form.control}
                name={`conditions.${index}.operator`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="required">Condition</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={true}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {OPERATORS.map((op) => (
                          <SelectItem key={String(op.value)} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="">
              <ConditionValueBasedOnParameter index={index} />
            </div>
          </div>

          <div className="relative h-4 group-last:hidden">
            <div className="absolute top-2 w-full border-b"></div>
            <div className="absolute -top-1.5 left-3/6 -translate-x-2/4">
              <span className="text-muted-foreground bg-white px-2 text-xs">AND</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ConditionValueBasedOnParameter({ index }: { index: number }) {
  const isInitialRender = useRef(true);

  const form = useFormContext<EventRuleFormData>();

  const watchRuleType = form.watch('ruleType');
  const watchDeviceType = form.watch('deviceType');
  const watchConditionParameter = Number(form.watch(`conditions.${index}.fact`));

  useEffect(() => {
    // skip initial render to avoid clearing user input during component mount
    if (!isInitialRender.current) {
      form.setValue(`conditions.${index}.value`, '');
    }

    isInitialRender.current = false;

    return () => {
      isInitialRender.current = true;
    };
  }, [watchConditionParameter]);

  if (watchRuleType === 'device' && watchDeviceType) {
    if (watchConditionParameter === COMMON_DEVICE_FIELDS['Event Status']) {
      let OPTIONS: {
        id: string | number;
        name: string;
      }[] = [];

      if (watchDeviceType) {
        const deviceTypeId = DEVICE_TYPE_CODE_BY_NAME[watchDeviceType];
        const eventDeviceMetadata = DEVICE_EVENTS_METADATA[deviceTypeId];

        OPTIONS = Object.values(eventDeviceMetadata.parametersByCode)
          .map((param) => ({
            id: param?.id,
            name: param?.name,
          }))
          .sort((a, b) => a?.name?.localeCompare(b?.name));
      }

      return (
        <FormField
          control={form.control}
          name={`conditions.${index}.value`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="required">Value</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={true}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select value" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {OPTIONS?.map((op) => (
                    <SelectItem key={String(op.id)} value={String(op.id)}>
                      {op.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.value`}
        render={({ field }) => (
          <FormItem className="mb-2">
            <FormLabel className="required">Value</FormLabel>
            <FormControl>
              <Input type="number" placeholder="Enter value" {...field} disabled={true} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (watchRuleType === 'plant') {
    let OPTIONS: {
      id: string | number;
      name: string;
    }[] = [];

    if (watchConditionParameter === PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status']) {
      const parameterId = PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status'];

      OPTIONS = Object.values(PLANT_FIELDS_METADATA[parameterId].parametersByCode)
        .map((param) => ({
          id: param?.id,
          name: param?.name,
        }))
        .sort((a, b) => a?.name?.localeCompare(b?.name));
    }

    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.value`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="required">Value</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value} disabled={true}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {OPTIONS?.map((op) => (
                  <SelectItem key={String(op.id)} value={String(op.id)}>
                    {op.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return (
    <FormField
      control={form.control}
      name={`conditions.${index}.value`}
      render={({ field }) => (
        <FormItem className="mb-2">
          <FormLabel className="required">Value</FormLabel>
          <FormControl>
            <Input type="number" placeholder="Enter value" {...field} disabled={true} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
